<template>
  <v-card flat>
    <!-- Datatable filter -->
    <v-card-title v-if="showGridFilter">
      <v-layout row wrap>
        <v-flex sm6>
          <v-text-field append-icon="filter_list" :label="messages.label.XPATH_MAPPINGS_FILTER" single-line clearable
            hide-details @input="value => filterXpathMappingsList(value)" v-model="filterInput">
          </v-text-field>
        </v-flex>
      </v-layout>
    </v-card-title>
    <!-- Data Table -->
    <v-card-text>
      <v-data-table :headers="headers" :items="filteredMappings" :pagination.sync="pagination"
        :loading="mappingsLoading" :total-items="totalItems" :rows-per-page-items="rowsPerPagesArray">
        <!-- Items -->
        <template slot="items" slot-scope="props">
          <tr @click="showCurrentXPathMappingDetails(props.item)"
            :active="!!selectedMaping && selectedMaping.id == props.item.id" class="pointer">
            <td class="text-xs-left">{{ props.item.title }}</td>
            <td class="text-xs-left">{{ props.item.scheme }}</td>
            <td class="text-xs-left">{{ props.item._enabled }}</td>
            <td class="text-xs-left">{{ props.item._added }}</td>
            <td class="text-xs-left">{{ props.item._updated }}</td>
          </tr>
        </template>
        <!-- No Data -->
        <template slot="no-data">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            {{ messages.info.NO_XPATH_MAPPINGS }}
          </v-alert>
        </template>
        <!-- No Results -->
        <template slot="no-results">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            {{ messages.info.NO_FILTER_XPATH_MAPPINGS }}
          </v-alert>
        </template>
        <!-- Page Text -->
        <template slot="pageText" slot-scope="props">
          {{ props.pageStart }} - {{ props.pageStop }} of
          {{ props.itemsLength }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from "vuex"
import messages from "@/MetadataRules/messages.json"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import {
  MVATS_WEB_SERVICE_URL,
  RULE_MGMT_DATA_SERVICE_URL
} from "@/store/constants/getter-types"
import AppService from "@/AppService"

/**
 * initialData
 */
function initialData(instance) {
  return {
    filterInput: "",
    mappings: [],
    filteredMappings: [],
    selectedMaping: {},
    mappingsLoading: false,
    pagination: {
      sortBy: "title",
      page: 1,
      rowsPerPage: 25,
      descending: false
    },
    totalItems: 0,
    rowsPerPagesArray: [25, 50, 75, 100],
    headers: instance.getTableHeaders()
  }
}

export default {
  name: "XPathMappingsList",
  data: function () {
    return initialData(this)
  },
  computed: {
    ...mapState(["mpxParentAccount"]),
    ...mapGetters([MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL]),
    showGridFilter() {
      return this.mappings.length > 0
    }
  },
  watch: {
    /**
     * Watch handler for pagination object
     */
    pagination: {
      handler(newVal, oldVal) {
        if (oldVal !== newVal) {
          // NOTE: Initial load triggers a change on 'totalItems' property
          this.retrieveXpathMappings()
        }
      },
      deep: true
    }
  },
  methods: {
    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
    },

    /**
     * getTableHeaders - Construct array of objects to show table headers
     *
     * @return {array}
     */
    getTableHeaders() {
      let headers = [
        {
          text: messages.label.TH_TITLE,
          align: "left",
          sortable: false,
          value: "title"
        },
        {
          text: messages.label.TH_SCHEME,
          align: "left",
          sortable: false,
          value: "scheme"
        },
        {
          text: messages.label.ENABLED,
          align: "left",
          sortable: false,
          value: "_enabled"
        },
        {
          text: messages.label.TH_ADDED,
          align: "left",
          sortable: false,
          value: "_updated"
        },
        {
          text: messages.label.TH_UPDATED,
          align: "left",
          sortable: false,
          value: "_updated"
        }
      ]
      return headers
    },

    /**
     * filterXpathMappingsList - Filter search result with text value
     * NOTE: This filters available search results on the client
     * @param {string} search
     */
    filterXpathMappingsList(search) {
      if (search) {
        search = search.toString().toLowerCase()
        const displayedKeys = [
          "title",
          "scheme",
          "_enabled",
          "_updated",
          "_added"
        ]
        this.filteredMappings = this.mappings.filter(obj => {
          let filteredObj = this.$lodash.pick(obj, displayedKeys)
          return Object.keys(filteredObj).some(function (key) {
            if (filteredObj[key] !== null) {
              return filteredObj[key]
                .toString()
                .toLowerCase()
                .includes(search)
            }
          })
        })
      } else {
        this.filteredMappings = this.mappings
      }
    },

    /**
     * Get pagination params
     */
    getPageParams(pagination) {
      let startRange
      let endRange
      if (pagination.page === 1) {
        startRange = 1
        endRange = pagination.rowsPerPage
      } else {
        startRange = (pagination.page - 1) * pagination.rowsPerPage + 1
        endRange = startRange + pagination.rowsPerPage - 1
      }
      return startRange + "-" + endRange
    },

    /**
     * getSortParams - Construct sorting param
     *
     * @param  {object} pagination pagination object
     * @return {string}            ex: '<filedName>|desc'
     */
    getSortParams(pagination) {
      let sortParams = ""
      sortParams =
        !this.$lodash.isNull(pagination.sortBy) &&
          !this.$lodash.isEmpty(pagination.sortBy)
          ? pagination.sortBy
          : "updated|desc"
      if (pagination.descending) {
        sortParams += "|desc"
      }
      return sortParams
    },

    /**
     * retrieveXpathMappings - Retrieve all XPath mappings
     */
    retrieveXpathMappings() {
      let queryObject = {
        range: this.getPageParams(this.pagination),
        sort: this.getSortParams(this.pagination),
        count: true,
        account: this.mpxParentAccount.id
      }
      this.mappings = []
      this.mappingsLoading = true
      return metadataRulesDataService
        .getXPathMapping(queryObject, this.RULE_MGMT_DATA_SERVICE_URL)
        .then(response => {
          if (!this.$lodash.isUndefined(response.data.entries)) {
            this.mappings = response.data.entries
            this.filterXpathMappingsList(this.filterInput)
            this.totalItems = response.data.totalResults
            this.mappings = MetadataRulesService.addClientOnlyFields(
              this.mappings
            )
          } else {
            this.mappings = []
          }
          return this.mappings
        })
        .catch(error => {
          this.mappings = []
          this.totalItems = 0
          return Promise.reject(error)
        })
        .finally(() => {
          this.mappingsLoading = false
        })
    },

    /**
     * refreshProviderGroupsList - Update provider groups list
     * @param {string} reason
     * @param {string} xpathMappingId
     */
    refreshXpathMappingsList(reason, xpathMappingId) {
      if (reason === "add") {
        // FIXME: changing pagination object shoult trigger
        // 'watch' but doesn't seem to detect changes propertly
        // calling 'retrieveProviders' for now
        this.pagination.sortBy = "added"
        this.pagination.descending = true
      } else if (reason === "update") {
        this.pagination.sortBy = "updated"
        this.pagination.descending = true
      } else if (reason === "delete") {
        this.updateSelectedRow()
        this.pagination.sortBy = "title"
        this.pagination.descending = false
      }
      // Re-retrieve provider groups and display updated data on the details sheet
      this.retrieveXpathMappings().then(() => {
        if (xpathMappingId) {
          this.selectedMaping = this.$lodash.find(
            this.filteredMappings,
            item => {
              return item.id === xpathMappingId
            }
          )
          if (!this.$lodash.isUndefined(this.selectedMaping)) {
            MetadataRulesService.$emit(
              "event:showXPathMappingDetailsSheet",
              this.selectedMaping
            )
          }
        }
      })
    },

    /**
     * showCurrentXPathMappingDetails- Show selected provider gorup details
     * @param {object} xpathMapping
     */
    showCurrentXPathMappingDetails(xpathMapping) {
      this.updateSelectedRow(xpathMapping)
      MetadataRulesService.$emit(
        "event:showXPathMappingDetailsSheet",
        this.selectedMaping
      )
    },

    /**
     * updateSelectedRow - Update selected Provider Group obj when the user clicks to create a new provider group
     * @param {object} xpathMapping
     */
    updateSelectedRow(xpathMapping) {
      if (this.$lodash.isUndefined(xpathMapping)) {
        this.selectedMaping = {}
      } else {
        this.selectedMaping = xpathMapping
      }
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
    MetadataRulesService.$on(
      "event:refreshXpathMappingsList",
      this.refreshXpathMappingsList
    )
    MetadataRulesService.$on(
      "event:hideXPathMappingDetailsSheet",
      this.updateSelectedRow
    )
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    MetadataRulesService.$off(
      "event:updateXPathMapping",
      this.refreshXpathMappingsList
    )
    MetadataRulesService.$off(
      "event:hideXPathMappingDetailsSheet",
      this.updateSelectedRow
    )
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/MetadataRules/XpathMapping/XPathMappingsList.vue